/**
 * The handbook's document model: exactly the vocabulary `lib/html.ts` allows on
 * the server, and nothing else.
 *
 * This list IS the contract. A block the schema does not know is not an
 * unstyled block - it is deleted, silently, the first time an article passes
 * through the editor, because that is what a document model does with markup it
 * cannot represent. `roundTrip.test.ts` walks every seeded guide through this
 * list and back for exactly that reason.
 *
 * Deliberately omitted: `img` and `span`. Both are allowed by the server, and
 * no guide uses either - pictures go in slots, and there is no styling a span
 * would carry. Adding them later is one extension each; letting them in now
 * would mean an editor that offers a control the reader does not style.
 */
import StarterKit from "@tiptap/starter-kit";
import Code from "@tiptap/extension-code";
import Underline from "@tiptap/extension-underline";
import type { Extensions } from "@tiptap/core";
import { KbCallout, KbDo, KbMail } from "./boxes";
import { KbLink } from "./link";
import { KbCheckList, KbHeading, KbLede } from "./marked";
import { KbShot } from "./shotSlot";
import { KbTable, KbTableCell, KbTableHeader, KbTableRow } from "./table";

/**
 * Every extension the handbook editor runs.
 *
 * A function rather than a constant because tiptap extensions carry per-editor
 * state once configured, and two editors sharing one array is the kind of bug
 * that only shows up when the second one is opened.
 */
export function kbExtensions(): Extensions {
  return [
    StarterKit.configure({
      // Replaced below by versions that keep a class or an id.
      heading: false,
      paragraph: false,
      bulletList: false,
      // The handbook has its own link mark (a bare href, see `link.ts`) and its
      // own code mark (see below).
      link: false,
      underline: false,
      code: false,
    }),
    // Code that can also be bold. The stock mark excludes every other mark, so
    // `<strong>the flag <code>--dry</code> is safe</strong>` came back with the
    // flag no longer bold - the one difference in the corpus that was a real
    // change to the article rather than a change of shape.
    Code.extend({ excludes: "" }),
    KbHeading.configure({ levels: [2, 3, 4] }),
    KbLede,
    KbCheckList,
    Underline,
    KbLink,
    KbCallout,
    KbDo,
    KbMail,
    KbShot,
    KbTable,
    KbTableRow,
    KbTableHeader,
    KbTableCell,
  ];
}
